import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { Typography, TextField, InputAdornment, IconButton, Tooltip, Grid } from "@material-ui/core";
import {
	Page,
	PageContent,
	ScrollToTopOnMount,
	PaginationLoader,
	PopupSnackbar,
	SendEmailModal,
	PageHeaderListActions,
} from "frame/components";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import * as fromHealthSafetyForm from "features/health-safety-forms";
import { HOME_ROUTE } from "features/home/home-routes";
import InductionMinutesCard from "../cards/induction-minutes-card";
import {
	AppendInductionMinutesForm,
	EditInductionMinutesForm,
	PageHeaderTabs,
} from "features/health-safety-forms";
import { getPageResults, useIsOnline, useTerminology } from "utils";
import { safetyWiseApi } from "utils/safetyWiseApi";
import { initialJobState } from "features/health-and-safety/reducers/health-safety-reducer";
import { CardText } from "features/timesheet/components/timesheet-list";
import InfiniteScroll from "react-infinite-scroll-component";

const useStyles = makeStyles((theme) => ({
	root: {
		position: "relative",
		backgroundColor: "none",
	},
	filter: { display: "flex" },
	empty: {
		marginTop: theme.spacing(3),
	},
	helperTextIcon: {
		color: theme.palette.success.main,
	},
	search: {
		display: "flex"
	},
	gridContainer: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(3),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(3)
		}
	},
	filterGrid: {
		paddingTop: theme.spacing(1)
	},
}));

const InductionMinutesList = ({
	role,
	inductions,
	onDelete,
	onGetInductionList,
	currentOrganisationGUID,
	addInductionMinutesForm,
}) => {
	const isOnline = useIsOnline();
	const classes = useStyles();
	const { siteLabel } = useTerminology();
	const [showEditModal, setShowEditModal] = useState(false);
	const [showViewModal, setShowViewModal] = useState(false);
	const [showAppendModal, setShowAppendModal] = useState(false);
	const [showEmailModal, setShowEmailModal] = useState(false);
	const [isSendingEmail, setIsSendingEmail] = useState(false);
	const [selectedItemId, setSelectedItemId] = useState(null);
	const [siteId, setSiteId] = useState(null);
	const [errorMessage, setErrorMessage] = useState("");
	const [successMessage, setSuccessMessage] = useState("");
	const [searchFilter, setSearchFilter] = useState("");
	const [showFilters, setShowFilters] = useState(false);
	const [previousSaveSuccess, setPreviousSaveSuccess] = useState(false);
	const isInitialMount = useRef(true);

	const [inductionList, setInductionList] = useState(getPageResults(initialJobState));
	const [currentPage, setCurrentPage] = useState(1);
	const defaultPageSize = 10; // Reduced from 20 to improve initial load performance
	const [totalNumberOfRecords, setTotalNumberOfRecords] = useState(1);
	const [hasMoreInductions, setHasMoreInductions] = useState(true);
	const [isDoneFetching, setIsDoneFetching] = useState(false);
	const ref = useRef(null);
	const [height, setHeight] = useState(0);

	const empty = inductionList && inductionList.length <= 0;

	const onEdit = (id) => () => {
		setSelectedItemId(id);
		setShowEditModal(true);
	};
	const onAppend = (id) => () => {
		setSelectedItemId(id);
		setShowAppendModal(true);
	};
	const onView = (id) => () => {
		setSelectedItemId(id);
		setShowViewModal(true);
	};
	const onEmail = (id, siteId) => () => {
		setSelectedItemId(id);
		setSiteId(siteId);
		setShowEmailModal(true);
	};
	const handleApiError = (error) => {
		if (error.response && error.response.status === "401") {
			setErrorMessage("You are not authorized to perform this action.");
		} else if (error.response && error.response.status === "404") {
			setErrorMessage("The record no longer exists.");
		} else {
			setErrorMessage(
				"Something went wrong, please refresh the page and try again."
			);
		}
	};

	const isFetching = inductions.flag === "processing" && isOnline;
	// const isDoneFetching = inductions.flag === "done" || !isOnline;

	const setNewInductionState = (inductionResult, page) => {
		let newState = initialJobState;

		if (inductionResult && inductionResult.results && inductionResult.results.length > 0) {
			newState = {
				...newState,
				...inductionResult
			};
		}

		newState.refreshList = inductionResult.refreshList;
		newState.flag = "done";
		// If navigating to first page clear the results from inductionList otherwise add to the end of it
		if (page === 1) {
			setInductionList([...getPageResults(newState)]);
		} else {
			setInductionList([...inductionList, ...getPageResults(newState)]);
		}
		setIsDoneFetching(true);
	};

	const getInductions = (
		fromPageToRequest = currentPage, // fromPageToRequest is the page to request from the API,
		recordDeletedOrUpdated = false, // recordDeletedOrUpdated is a boolean to determine whether to overwrite or append to inductionLists
		pageSize = defaultPageSize // pageSize is the number of records to load from the API, can be paramaterised to request more data.
	) => {
		if (totalNumberOfRecords !== inductionList.length || recordDeletedOrUpdated) {
			if (recordDeletedOrUpdated) {
				setHasMoreInductions(true);
			}

			safetyWiseApi
				.post(`${currentOrganisationGUID}/induction-minutes/paged`, {
					ascending: false,
					page: fromPageToRequest,
					pageSize,
					orderBy: "createdAt",
					searchKey: searchFilter
				})
				.then((result) => {
					setNewInductionState(result.data, fromPageToRequest);

					// If more then the defaultPageSize data is requested from first page then dynamically set the current page, else set to fromPageToRequest
					if (pageSize > defaultPageSize && fromPageToRequest === 1) {
						setCurrentPage(pageSize / defaultPageSize);
					} else {
						setCurrentPage(fromPageToRequest);
					}

					setTotalNumberOfRecords(result.data.totalNumberOfRecords);
					if (
						result.data.totalNumberOfRecords ==
						inductionList.length + result.data.results.length
					) {
						setHasMoreInductions(false);
					}
				})
				.catch((error) => {
					console.error("Error fetching inductions:", error);
					setErrorMessage("Failed to load induction meetings. Please try again.");
					setIsDoneFetching(true);
					setHasMoreInductions(false);
				});
		} else {
			setHasMoreInductions(false);
		}
	};

	// The InfiniteScroll Component requires a height for it to work  effectively and for it resize relative to the parent.
	useEffect(() => {
		const handleResize = () => {
			if (ref.current) {
				setHeight(ref.current.offsetHeight - 20); // reduce height by 20px otherwise the scrollbar flows past the height of screen.
			}
		};
		handleResize();
		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	// Initial load - wait for currentOrganisationGUID to be available
	useEffect(() => {
		if (currentOrganisationGUID) {
			getInductions();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentOrganisationGUID]);

	// Watch for form save success and refresh list
	useEffect(() => {
		if (addInductionMinutesForm?.save?.success && !previousSaveSuccess) {
			setPreviousSaveSuccess(true);
			// Refresh the list when a save succeeds
			if (selectedItemId) {
				const inductionIndex = inductionList.findIndex((t) => t.id === selectedItemId);
				const pageSizeToLoad =
					inductionIndex >= 0
						? Math.ceil((inductionIndex + 1) / defaultPageSize) * defaultPageSize
						: defaultPageSize;
				getInductions(1, true, pageSizeToLoad);
			} else {
				getInductions(1, true);
			}
		} else if (!addInductionMinutesForm?.save?.success && previousSaveSuccess) {
			setPreviousSaveSuccess(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [addInductionMinutesForm?.save?.success, previousSaveSuccess]);

	// Debounced search effect - triggers search after user stops typing
	useEffect(() => {
		// Skip debounce on initial mount - initial load is handled by the first useEffect
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}

		const searchTimeout = setTimeout(() => {
			// Reset to first page when search changes
			setCurrentPage(1);
			setInductionList([]);
			setTotalNumberOfRecords(1);
			setHasMoreInductions(true);
			setIsDoneFetching(false);
			getInductions(1, true);
		}, 500); // 500ms debounce

		return () => clearTimeout(searchTimeout);
	}, [searchFilter]);

	return (
		<Page className={classes.root} title="Induction Meetings" style={{ height: "100%" }}>
			<div ref={ref} style={{ backgroundColor: "", height: "100%", overflow: "hidden" }}>
				{/* <p>
					{totalNumberOfRecords} {inductionList.length}
				</p> */}
				{/* n.o of records and length of loaded list */}
				<InfiniteScroll
					dataLength={inductionList.length}
					next={() => {
						getInductions(currentPage + 1);
					}}
					hasMore={hasMoreInductions}
					loader={<CardText message={"Loading..."} />}
					height={height + "px"}
					scrollThreshold={0.99}
					style={{ paddingBottom: "10px" }}
				>
					<ScrollToTopOnMount />

											<PageHeaderTabs
							title="Induction Meetings"
							addTabPath={fromHealthSafetyForm.ADD_INDUCTION_MINUTES_PATH}
							hideListTabButton={true}
							backActionPath={HOME_ROUTE}
						
						titleAdornment={
						<PageHeaderListActions
							showFilters={showFilters}
							onToggleFilters={() => setShowFilters(!showFilters)}
						/>
					}>
							{/* START: Search */}
							{showFilters && (
								<div className={classes.gridContainer}>
									<Grid container spacing={2} className={classes.filterGrid}>
										<Grid item xs={12} md={6}>
											<TextField
												className={classes.search}
												id="input-with-icon-textfield"
												variant="outlined"
												label="Search"
												InputProps={{
													startAdornment: (
														<InputAdornment position="start">
															<SearchIcon />
														</InputAdornment>
													),
													endAdornment: searchFilter && (
														<InputAdornment position="end">
															<IconButton
																aria-label="clear search"
																onClick={() => {
																	setSearchFilter("");
																	setCurrentPage(1);
																	setInductionList([]);
																	setTotalNumberOfRecords(1);
																	setHasMoreInductions(true);
																	setIsDoneFetching(false);
																	getInductions(1, true);
																}}
																edge="end"
																size="small"
															>
																<ClearIcon />
															</IconButton>
														</InputAdornment>
													)
												}}
												value={searchFilter}
												onChange={(val) => {
													const { value } = val.target;
													setSearchFilter(value);
													// Real-time search with debounce effect will handle the API call
												}}
												style={{ backgroundColor: "white", maxWidth: 480, width: "100%" }}
												placeholder={`e.g. ${siteLabel} Name, Notes, People`}
											/>
										</Grid>
									</Grid>
								</div>
							)}
							{/* END: Search */}
						</PageHeaderTabs>
					<PageContent pad>
						{/* <PaginationHeader pagination={inductions} /> */}
						<p style={{ marginTop: 5 }}>
							{inductionList.length > 0 ? totalNumberOfRecords + " records" : "No records loaded"}
						</p>

						{!empty &&
							isDoneFetching &&
							inductionList.map((item, index) => (
								<InductionMinutesCard
									key={`induction-listing-${index}-${item.id}`}
									inductionMinutes={item}
									onAppend={onAppend(item.id)}
									onEdit={onEdit(item.id)}
									onView={onView(item.id)}
									onDelete={() => {
										//index + 1 to get position in array, then get the next n(pageSize)TH multiple which will be the page size to load
										const pageSizeToLoad =
										Math.ceil((index + 1) / defaultPageSize) * defaultPageSize;

										safetyWiseApi
											.del(`${currentOrganisationGUID}/induction-minutes/delete/${item.id}`)
											.then(() => {
												getInductions(1, true, pageSizeToLoad);
											})
											.catch((error) => {
												console.error("Error deleting induction:", error);
												handleApiError(error);
											});
									}}
									onEmail={onEmail(item.id, item.siteId)}
									role={role}
								/>
							))}

						{isFetching && <PaginationLoader height={90} pagination={inductions} />}

						{empty && isDoneFetching && (
							<Typography className={classes.empty} variant="body1">
								There are currently no Induction Meetings listed.
							</Typography>
						)}

						{/* <Pagination
							onGetList={onGetInductionList}
							pagination={inductions}
							orderBy="createdAt"
							ascending={false}
							searchKey={searchFilter}
						/> */}
					</PageContent>

					{showEditModal && (
						<EditInductionMinutesForm
							id={selectedItemId}
							inductionList={inductionList}
							onClose={(submitted = false) => {
								setShowEditModal(false);
								if (submitted) {
									const inductionIndex = inductionList.findIndex((t) => t.id === selectedItemId);
									const pageSizeToLoad =
										Math.ceil((inductionIndex + 1) / defaultPageSize) * defaultPageSize;
									getInductions(1, true, pageSizeToLoad);
								}
							}}
						/>
					)}
					{showAppendModal && (
						<AppendInductionMinutesForm
							id={selectedItemId}
							values={inductionList.find((x) => x.id == selectedItemId)}
							inductionList={inductionList}
							onClose={(submitted = false) => {
								setShowAppendModal(false);
								if (submitted) {
									console.log("get here");
									const inductionIndex = inductionList.findIndex((t) => t.id === selectedItemId); // find inductionIndex
									//inductionIndex + 1 to get position in array, then get the next n(pageSize)TH multiple which will be the page size to load
									const pageSizeToLoad =
										Math.ceil((inductionIndex + 1) / defaultPageSize) * defaultPageSize;

									getInductions(1, true, pageSizeToLoad);
								}
							}}
						/>
					)}
					{showViewModal && (
						<EditInductionMinutesForm
							readOnly
							id={selectedItemId}
							inductionList={inductionList}
							onClose={() => setShowViewModal(false)}
						/>
					)}
			<SendEmailModal
				isOpen={showEmailModal}
				title={"Email Induction Meeting Record"}
				onClose={() => setShowEmailModal(false)}
				onSend={(selectedRecipients) => {
					setIsSendingEmail(true);
					safetyWiseApi
						.post(
							`${currentOrganisationGUID}/induction-minutes/email/${selectedItemId}`,
							{
								toInternalAddresses:
									selectedRecipients.recipients,
								toExternalAddresses: [
									...selectedRecipients.recipientsOther,
									...selectedRecipients.recipientsCustomerSites,
								],
							}
						)
						.then(() => {
							setSuccessMessage("Emails sent successfully.");
						})
						.catch(handleApiError)
						.finally(() => {
							setIsSendingEmail(false);
							setShowEmailModal(false);
						});
				}}
				isSaving={isSendingEmail}
				selectedItemId={selectedItemId}
				siteId={siteId}
				emailListType={"relatedToParticularSite"}
			/>
					<PopupSnackbar
						message={errorMessage}
						coloured
						error
						onExited={() => setErrorMessage("")}
					/>
					<PopupSnackbar
						message={successMessage}
						coloured
						success
						onExited={() => setSuccessMessage("")}
					/>
					{!isDoneFetching && <CardText message={"Loading..."} style={{ marginTop: "20px" }} />}
					{!hasMoreInductions && <CardText message={"All induction meetings have been loaded."} />}
					{inductionList.length === 0 && isDoneFetching && (
						<CardText message={"No induction meetings to show."} />
					)}
				</InfiniteScroll>
			</div>
		</Page>
	);
};

InductionMinutesList.propTypes = {
	role: PropTypes.string.isRequired,
	inductions: PropTypes.object.isRequired,
	onDelete: PropTypes.func.isRequired,
	onGetInductionList: PropTypes.func.isRequired,
	currentOrganisationGUID: PropTypes.string,
	addInductionMinutesForm: PropTypes.object,
};

export default InductionMinutesList;
